import { z } from 'zod';
import { BaseResponseSchema, } from './primitives.js';
export const MLModelTypeSchema = z.enum(['qwen-vl', 'tesseract', 'cnn']);
export const MLTaskTypeSchema = z.enum(['ocr', 'extract', 'analyze', 'features']);
export const QwenVLConfigSchema = z.object({
    maxTokens: z.number().min(50).max(2048).default(512),
    temperature: z.number().min(0).max(2).default(0.1),
    topP: z.number().min(0).max(1).default(0.9),
    doSample: z.boolean().default(false),
    prompts: z.object({
        ocr: z.string().default('Extract all text from this wafer image. Be precise with IDs and serial numbers.'),
        extract: z.string().default('Extract structured information from wafer image: Wafer ID, Lot Number, Serial Number, Date. Format as JSON.'),
        analyze: z.string().default('Analyze this wafer image and provide detailed information about visible text, markings, and identifiers.'),
    }),
});
export const TesseractConfigSchema = z.object({
    lang: z.string().default('eng'),
    psm: z.number().min(0).max(13).default(3),
    oem: z.number().min(0).max(3).default(3),
    dpi: z.number().min(70).max(600).default(300),
    preprocessingSteps: z
        .array(z.enum(['denoise', 'threshold', 'deskew', 'contrast']))
        .default(['denoise', 'threshold']),
});
export const CNNConfigSchema = z.object({
    inputSize: z.object({
        width: z.number().default(224),
        height: z.number().default(224),
    }),
    extractFeatures: z.boolean().default(true),
    featureLayer: z.string().optional(),
});
export const OcrMlProviderSettingsSchema = z.object({
    enabled: z.boolean().default(true),
    primaryModel: MLModelTypeSchema.default('qwen-vl'),
    fallbackModels: z.array(MLModelTypeSchema).default(['tesseract']),
    endpoints: z.object({
        qwenVL: z.string().optional(),
        tesseract: z.string().optional(),
        cnn: z.string().optional(),
    }),
    qwenVLConfig: QwenVLConfigSchema.optional(),
    tesseractConfig: TesseractConfigSchema.optional(),
    cnnConfig: CNNConfigSchema.optional(),
    validation: z.object({
        minConfidence: z.number().min(0).max(100).default(70),
        enableMultiModelValidation: z.boolean().default(false),
        consensusThreshold: z.number().min(0).max(1).default(0.7),
        weightings: z.object({
            qwenVL: z.number().min(0).max(1).default(0.6),
            tesseract: z.number().min(0).max(1).default(0.3),
            cnn: z.number().min(0).max(1).default(0.1),
        }),
    }),
    performance: z.object({
        timeout: z.number().min(1000).max(60000).default(30000),
        maxConcurrentRequests: z.number().min(1).max(100).default(10),
        enableCaching: z.boolean().default(true),
        cacheTTL: z.number().min(60).max(86400).default(3600),
        batchSize: z.number().min(1).max(100).default(1),
    }),
});
export const MLValidationResultSchema = z.object({
    success: z.boolean(),
    extractedText: z.string().optional(),
    structuredData: z.record(z.unknown()).optional(),
    confidence: z.number().min(0).max(100),
    modelUsed: MLModelTypeSchema,
    fallbackUsed: z.boolean().default(false),
    processingTime: z.number(),
    features: z.array(z.number()).optional(),
    metadata: z.object({
        attempts: z.number(),
        models: z.array(MLModelTypeSchema),
        timestamp: z.string(),
    }),
});
export const MlProviderIdParamSchema = z.object({
    providerId: z.coerce.number().int().positive(),
});
export const MlHealthQuerySchema = z.object({
    providerId: z.coerce.number().int().positive().optional(),
});
export const AnalyzeImageRequestSchema = z.object({
    imageBase64: z.string().optional(),
    s3Uri: z.string().optional(),
    task: MLTaskTypeSchema.default('extract'),
    enableFallback: z.boolean().default(true),
    useMultiModelValidation: z.boolean().default(false),
});
export const AnalyzeImageResponseSchema = z.object({
    success: z.boolean(),
    data: MLValidationResultSchema.optional(),
    message: z.string().optional(),
    error: z.string().optional(),
});
export const AnalyzeBatchRequestSchema = z.object({
    images: z.array(z.object({
        id: z.string(),
        imageBase64: z.string().optional(),
        s3Uri: z.string().optional(),
    })),
    task: MLTaskTypeSchema.default('extract'),
    enableFallback: z.boolean().default(true),
});
export const BatchMLResponseSchema = z.object({
    results: z.array(z.object({
        id: z.string(),
        result: MLValidationResultSchema.optional(),
        error: z.string().optional(),
    })),
    totalProcessed: z.number(),
    successCount: z.number(),
    failureCount: z.number(),
    totalProcessingTime: z.number(),
});
export const AnalyzeBatchResponseSchema = z.object({
    success: z.boolean(),
    data: BatchMLResponseSchema.optional(),
    message: z.string().optional(),
    error: z.string().optional(),
});
export const MLConfigResponseSchema = z.object({
    success: z.boolean(),
    data: OcrMlProviderSettingsSchema.optional(),
    message: z.string().optional(),
    error: z.string().optional(),
});
export const MlConfigUpdateBodySchema = OcrMlProviderSettingsSchema.partial();
export const MlConfigUpdateResponseSchema = BaseResponseSchema.extend({
    message: z.string(),
});
export const MLHealthResponseSchema = z.object({
    success: z.boolean(),
    data: z
        .object({
        healthy: z.boolean(),
        endpoints: z.record(z.boolean()),
        config: OcrMlProviderSettingsSchema,
    })
        .optional(),
    message: z.string().optional(),
    error: z.string().optional(),
});
//# sourceMappingURL=ml-sagemaker.schemas.js.map